/**
 * Every public route's metadata, in one table.
 *
 * Three things used to decide independently what a page's metadata was: the
 * page itself (via usePageSeo), `index.html`, and App's initialization effect —
 * which is why the document title flickered between two different values on
 * load, and why `routeSeo.ts` had to exist to referee them. There is one table
 * now, and one writer (`components/SeoHead.tsx`).
 *
 * The table also feeds two build-time consumers: the sitemap generator and the
 * prerender crawler (`plugins/seoAssets.ts`, `scripts/prerender.mjs`). A route
 * added to `AppRoutes` without an entry here would silently be absent from both,
 * so `seo.test.ts` reads the router and fails when the two disagree.
 *
 * Most entries import the `seo` object the page already had, so the copy stays
 * where the rest of that page's copy lives, in `src/content/waq/*`.
 */
import { seo as aboutSeo, schema as aboutSchema } from '../content/waq/about';
import { seo as caseChatbotSeo } from '../content/waq/caseChatbotTesting';
import { seo as caseOnDeviceSeo } from '../content/waq/caseOnDeviceAi';
import { seo as caseStudiesHubSeo } from '../content/waq/caseStudiesHub';
import { seo as caseTestGenerationSeo } from '../content/waq/caseTestGeneration';
import { seo as homeSeo } from '../content/waq/home';
import { seo as industriesHubSeo } from '../content/waq/industriesHub';
import { seo as industryCommunicationsSeo } from '../content/waq/industryCommunications';
import { seo as industryEcommerceSeo } from '../content/waq/industryEcommerce';
import { seo as industryFintechSeo } from '../content/waq/industryFintech';
import { seo as industryHealthcareSeo } from '../content/waq/industryHealthcare';
import { seo as industryPharmaSeo } from '../content/waq/industryPharma';
import { seo as qaConsultingSeo } from '../content/waq/qaConsultingPage';
import { seo as qaTalentSeo, talentServiceJsonLd } from '../content/waq/qaTalent';
import { seo as qualityOperationsSeo } from '../content/waq/qualityOperationsPage';
import { seo as testArchitectureSeo } from '../content/waq/testArchitecturePage';
import { pageHero as aiDrivenHero } from '../content/waq/aiDrivenValidation';
import {
  aiDrivenValidation as aiDrivenService,
  qaConsulting as qaConsultingService,
  qualityOperations as qualityOperationsService,
  testArchitecture as testArchitectureService,
} from '../content/waq/services';
import { SITE_URL } from './site';
import { organizationJsonLd, serviceJsonLd } from './structuredData';

/** Defined in `./site` so the structured-data builders can use it without a cycle. */
export { SITE_URL };

/**
 * The social card: the wordmark on white, 1200x800, 25 KB.
 *
 * It replaced /sm-logo.png, a 2 MB PNG that was 95% transparent -- so each
 * platform drew it on whatever background it liked, and WhatsApp, which will
 * not preview an image much over 300 KB, drew nothing at all. White is what
 * LinkedIn already composited it onto. The old file stays in public/ for links
 * that were shared with it; nothing on the site references it any more.
 */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 800;

export interface PageSeo {
  title: string;
  description: string;
  /** Absolute URL. Falls back to the site-wide card. */
  ogImage?: string;
  /** Defaults to `index, follow`. A staging build forces noindex regardless. */
  robots?: string;
  /**
   * Short label for the breadcrumb trail. The SEO title is written for a search
   * result, which is too long to read as a crumb. Falls back to `title`.
   */
  breadcrumb?: string;
  /** `article`, for the blog. Everything else is a `website`. */
  ogType?: 'website' | 'article';
  /** Page-specific JSON-LD, e.g. the Service on /for-employers. Breadcrumbs are added for every page. */
  jsonLd?: Record<string, unknown>[];
}

export const PAGE_SEO: Record<string, PageSeo> = {
  '/': { ...homeSeo, breadcrumb: 'Home' },

  /*
   * The company node as well as the page's own: Google reads Organization markup
   * from the home page or an about page, and this is the about page. It is the
   * same `organizationJsonLd()` the home page emits, so the two cannot disagree.
   */
  '/about': {
    ...aboutSeo,
    breadcrumb: 'About',
    jsonLd: [aboutSchema.aboutPage, organizationJsonLd(), aboutSchema.person],
  },
  '/contact': {
    title: 'Contact WeAreQA — Talk to a QA Engineer',
    description:
      'Book a call with a QA engineer or send us a brief. Offices in Lviv and Krakow. We respond within one business day.',
    breadcrumb: 'Contact',
  },

  '/blog': {
    title: 'QA Insights & Resources — WeAreQA Blog',
    description:
      'Expert insights, career advice and industry trends for QA engineers and the teams that hire them.',
    breadcrumb: 'Blog',
  },

  /*
   * The four pages below had no metadata of their own at all: they rendered
   * under the site-wide title, so search results listed four different pages
   * under one identical heading and description.
   */
  '/for-qa-engineers': {
    title: 'QA Engineer Jobs — Remote Roles with Global Teams | WeAreQA',
    description:
      'Remote QA engineering roles with global product teams. Personalised support through interviews, salary negotiation and onboarding — no agency middleman.',
    breadcrumb: 'For QA engineers',
  },
  '/for-employers': {
    ...qaTalentSeo,
    breadcrumb: 'Hire QA talent',
    jsonLd: [talentServiceJsonLd()],
  },
  '/apply-qa-engineer': {
    title: 'Apply as a QA Engineer — Join WeAreQA',
    description:
      'Apply to join the WeAreQA engineering network. Tell us about your testing experience and we will match you with remote roles that fit it.',
    breadcrumb: 'Apply',
  },
  /*
   * The detailed form, kept for companies that want to send a full brief. Out
   * of the index: /for-employers answers the same searches, and two pages
   * competing for "hire QA engineers" would split them.
   */
  '/hire-qa-talent': {
    title: 'Send a Detailed QA Hiring Brief — WeAreQA',
    description:
      'Send WeAreQA a full job description for the QA engineer you need. Direct hire carries no recruitment or placement fee.',
    breadcrumb: 'Hire QA talent',
    robots: 'noindex, follow',
  },

  '/ai-testing-use-cases': {
    title: 'AI Testing Use Cases | WeAreQA @ AI Week Milan 2026',
    description:
      "WeAreQA's AI testing methodologies, showcased at AI Week Milan 2026: chatbot testing, on-device AI validation and AI-powered test generation.",
    breadcrumb: 'AI testing use cases',
  },

  /* Services. `/services` itself redirects to the first one and has no page. */
  /*
   * Each service page also says, in JSON-LD, which service it describes and who
   * provides it. The name is the service's own name from the engagement models,
   * the description the same text as the meta description.
   */
  '/services/ai-driven-validation': {
    title: aiDrivenService.model.title,
    description: aiDrivenHero.lead,
    breadcrumb: 'AI-driven validation',
    jsonLd: [
      serviceJsonLd({
        path: aiDrivenService.href,
        name: aiDrivenService.model.title,
        description: aiDrivenHero.lead,
      }),
    ],
  },
  '/services/qa-architecture-engineering': {
    ...testArchitectureSeo,
    breadcrumb: 'QA architecture engineering',
    jsonLd: [
      serviceJsonLd({
        path: testArchitectureService.href,
        name: testArchitectureService.title,
        description: testArchitectureSeo.description,
      }),
    ],
  },
  '/services/qa-consulting-modernization': {
    ...qaConsultingSeo,
    breadcrumb: 'QA consulting & modernization',
    jsonLd: [
      serviceJsonLd({
        path: qaConsultingService.href,
        name: qaConsultingService.model.title,
        description: qaConsultingSeo.description,
      }),
    ],
  },
  '/services/intelligent-quality-operations': {
    ...qualityOperationsSeo,
    breadcrumb: 'Intelligent quality operations',
    jsonLd: [
      serviceJsonLd({
        path: qualityOperationsService.href,
        name: qualityOperationsService.model.title,
        description: qualityOperationsSeo.description,
      }),
    ],
  },

  /* Industries. */
  '/industries': { ...industriesHubSeo, breadcrumb: 'Industries' },
  '/industries/fintech': { ...industryFintechSeo, breadcrumb: 'Fintech' },
  '/industries/healthcare': { ...industryHealthcareSeo, breadcrumb: 'Healthcare' },
  '/industries/pharma': { ...industryPharmaSeo, breadcrumb: 'Pharma' },
  '/industries/ecommerce': { ...industryEcommerceSeo, breadcrumb: 'eCommerce' },
  '/industries/communications': { ...industryCommunicationsSeo, breadcrumb: 'Communications' },

  /* Case studies. */
  '/case-studies': { ...caseStudiesHubSeo, breadcrumb: 'Case studies' },
  '/case-studies/ai-chatbot-testing': { ...caseChatbotSeo, breadcrumb: 'AI chatbot testing' },
  '/case-studies/on-device-ai-testing': { ...caseOnDeviceSeo, breadcrumb: 'On-device AI testing' },
  '/case-studies/ai-test-generation': {
    ...caseTestGenerationSeo,
    breadcrumb: 'AI test generation',
  },

  /*
   * Indexed like everything else. These carried `noindex, follow` at first, on
   * a crawl-budget argument that does not survive contact with the numbers:
   * there are 25 pages here, and Google was already crawling the site
   * thousands of times a day. Legal pages are ordinarily indexed — they are a
   * trust signal, and people do search for a named company's privacy policy.
   */
  '/privacy-policy': {
    title: 'Privacy Policy — WeAreQA',
    description:
      'How WeAreQA collects, uses and protects the personal data of QA engineers and employers who use this site.',
    breadcrumb: 'Privacy policy',
  },
  '/terms-of-service': {
    title: 'Terms of Service — WeAreQA',
    description: 'The terms under which WeAreQA provides this website and its services.',
    breadcrumb: 'Terms of service',
  },
};
/**
 * The brand at the end of a title that lacks it -- where it fits.
 *
 * Fourteen titles named no brand at all. Google shows a result's site name
 * separately (from the WebSite markup on the home page), but the title is still
 * what a searcher reads first, and "| WeAreQA" is the convention the newest
 * pages already follow. Only where the whole title stays within 60 characters,
 * about where a result is cut: on a longer title the suffix would push the
 * words that say what the page is past the cut. The titles themselves are the
 * pages' own copy and are not rewritten here.
 */
const BRAND_SUFFIX = ' | WeAreQA';
const TITLE_BUDGET = 60;

for (const entry of Object.values(PAGE_SEO)) {
  if (
    !entry.title.includes('WeAreQA') &&
    entry.title.length + BRAND_SUFFIX.length <= TITLE_BUDGET
  ) {
    entry.title += BRAND_SUFFIX;
  }
}

/**
 * Routes `AppRoutes` declares that deliberately have no entry above, with the
 * reason. `seo.test.ts` reads this: an unlisted, unexplained route is a failure,
 * because that is how a page ends up missing from the sitemap unnoticed.
 */
export const SEO_EXEMPT_ROUTES: Record<string, string> = {
  '/admin-qa-management-portal': 'Redirect to the dashboard.',
  '/admin-qa-management-portal/*': 'Private panel. Disallowed in robots.txt.',
  '/deck/ai-native-engineering': 'Unlisted presentation. Sets its own noindex.',
  '/services': 'Redirect to /services/ai-driven-validation.',
  '/industry': 'Redirect to /industries.',
  '/blog/article/:slug': 'Dynamic. Metadata comes from the article; sitemap-blog.xml lists them.',
  '/consent-demo': 'Development only; not mounted in a production build.',
  '*': 'Catch-all, renders the 404 page.',
};

/**
 * Routes whose metadata is set by the page itself, because it depends on data
 * the table cannot know. `SeoHead` leaves these alone; anything else missing
 * from the table is treated as a 404.
 */
export const PAGE_OWNED_SEO_PREFIXES = ['/blog/article/', '/deck/', '/admin-qa-management-portal'];

export function routeOwnsItsSeo(pathname: string): boolean {
  return PAGE_OWNED_SEO_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** The 404 page is not a route, so it carries its metadata here. */
export const NOT_FOUND_SEO: PageSeo = {
  title: 'Page Not Found — WeAreQA',
  description: 'That page does not exist. Browse services, industries and case studies instead.',
  robots: 'noindex, nofollow',
};

/** Every route with a page, in table order. Used by the prerender crawler. */
export const SEO_PATHS = Object.keys(PAGE_SEO);

/** The subset that belongs in `sitemap-pages.xml`. */
export const SITEMAP_PATHS = SEO_PATHS.filter(
  (path) => !(PAGE_SEO[path].robots ?? '').includes('noindex')
);

export function resolvePageSeo(pathname: string): PageSeo | undefined {
  // A trailing slash is the same page; nginx does not redirect it away.
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PAGE_SEO[normalized];
}

/**
 * Ancestors of `pathname` that are real pages, nearest last. `/industries/fintech`
 * gives `/` then `/industries`; `/services/ai-driven-validation` gives only `/`,
 * because `/services` is a redirect and a crumb pointing at one is a dead end.
 */
export function breadcrumbTrail(pathname: string): { path: string; name: string }[] {
  const segments = pathname.split('/').filter(Boolean);
  const trail: { path: string; name: string }[] = [];

  for (let depth = 0; depth <= segments.length; depth += 1) {
    const path = depth === 0 ? '/' : `/${segments.slice(0, depth).join('/')}`;
    const entry = PAGE_SEO[path];
    if (entry) trail.push({ path, name: entry.breadcrumb ?? entry.title });
  }

  return trail;
}
